import type { ApiClient, FieldSubject } from '@carbonwebmail/api-client'
import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { useCallback, useEffect, useState } from 'react'

/**
 * The columns an organisation declared, and what a heading menu does to one.
 *
 * Both boards have declared columns and the three acts are identical on them:
 * load the declarations, rename-and-retype one, stop offering one. Only the
 * subject differs, and it decides nothing but which table the row is in.
 *
 * **Renaming cannot change who a campaign reaches.** A rule and a merge token
 * name a field's *key*, and no path here touches one.
 *
 * **Deleting stops offering a column and leaves its values.** Re-declaring the
 * key brings them back into view, which is what somebody who deleted the wrong
 * column needs — and stripping them would mean a mis-click destroys a column of
 * data across every row with nothing to restore from.
 *
 * A failure loading leaves the caller with no declared columns rather than no
 * board. That is the board as it was before anybody declared one: a worse
 * board, not a broken one.
 */
/**
 * As little of a column as the dialogs that change one need.
 *
 * Both boards' column types satisfy it, which is what lets one dialog serve
 * both: renaming a column and changing what it holds are the same act wherever
 * the column is.
 */
export interface EditableColumn {
  readonly label: string
  readonly kind: 'base' | 'field'
  /** The declared field's key, for a `field`. Absent for a built-in column. */
  readonly key: string | undefined
}

export function useDeclaredColumns({
  client,
  subject,
}: {
  readonly client: ApiClient
  readonly subject: FieldSubject
}) {
  const [fields, setFields] = useState<readonly ContactFieldDto[]>([])

  const load = useCallback(async () => {
    setFields((await client.declaredFields(subject).catch(() => ({ fields: [] }))).fields)
  }, [client, subject])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Renames a declared column and changes what it holds, in one write.
   *
   * Answers the reason it was refused, or nothing — the caller is a dialog that
   * needs something to show beside the field rather than an exception.
   */
  const edit = useCallback(
    async (
      key: string,
      change: {
        readonly label: string
        readonly type: ContactFieldDto['type']
        readonly choices: readonly string[]
      },
    ): Promise<string | undefined> => {
      try {
        const result = await client.editField(subject, key, {
          label: change.label,
          type: change.type,
          choices: [...change.choices],
        })
        if (result.status === 'invalid') return result.problem
        if (result.status === 'key-taken') return 'A column with that name already exists.'
        await load()
        return undefined
      } catch {
        return 'We could not save that column. Please try again in a moment.'
      }
    },
    [client, load, subject],
  )

  const remove = useCallback(
    async (key: string): Promise<string | undefined> => {
      try {
        await client.deleteField(subject, key)
        await load()
        return undefined
      } catch {
        return 'We could not delete that column. Please try again in a moment.'
      }
    },
    [client, load, subject],
  )

  return { fields, edit, remove, reload: load }
}

/** The declaration behind a column, for the calls that need its other half. */
export function fieldFor(
  fields: readonly ContactFieldDto[],
  key: string | undefined,
): ContactFieldDto | undefined {
  return fields.find((field) => field.key === key)
}
